import React from 'react';
import { Box, Drawer, DrawerContent, DrawerOverlay, Spinner, useMediaQuery } from '@chakra-ui/react';
import { useFlag } from '@unleash/proxy-client-react';
import cx from 'clsx';
import { motion, MotionProps } from 'framer-motion';

import { AppToggle } from '../../../clients/unleash/app-toggle-names';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks/app-hooks';
import { closeHistory } from '../../../redux/thunks/chat-tab-thunk';
import { isPortalWebMode } from '../../../utils/extension-mode';
import { useConversationsContext } from '../ConversationsContext';
import { ChatHistory } from '../History/ChatHistory';

import { Messages } from './Messages';

import theme from '../../../styles/chakra/theme.module.scss';
import styles from './MessageList.module.scss';

const webPortalMode = isPortalWebMode();
const chatHistoryMaxWidth = parseInt(theme.chatHistoryMaxWidth, 10);
const motionProps: MotionProps = {
    initial: false,
    variants: {
        opened: { width: chatHistoryMaxWidth, opacity: 1 },
        closed: { width: 0, opacity: 0 },
    },
    transition: { type: 'tween' },
};

export const MessageList: React.FC = () => {
    const chatHistoryEnabled = useFlag(AppToggle.CHAT_TAB_HISTORY);
    const dispatch = useAppDispatch();
    const [isLargerThan768] = useMediaQuery('(min-width: 768px)');

    const { activeConversationId, isLoadingChatsHistory } = useConversationsContext();

    const historyOpen = useAppSelector((state) => state.chatTab.historyOpen);
    const handleClose = () => dispatch(closeHistory('user'));

    if (!activeConversationId || isLoadingChatsHistory) {
        return (
            <Box m="auto">
                <Spinner />
            </Box>
        );
    }

    if (webPortalMode && !isLargerThan768) {
        return (
            <>
                <Drawer
                    id="chat-history-drawer"
                    returnFocusOnClose={false}
                    trapFocus={false}
                    isOpen={historyOpen}
                    placement="left"
                    onClose={handleClose}
                >
                    <DrawerOverlay />
                    <DrawerContent id="chat-history-pane" aria-expanded={historyOpen} fontSize="10px">
                        <ChatHistory />
                    </DrawerContent>
                </Drawer>
                <div className={styles.portalModeMobile}>
                    <Messages activeConversationId={activeConversationId} />
                </div>
            </>
        );
    }

    return (
        <div
            className={cx(styles.container, {
                [styles.portalMode]: webPortalMode,
                [styles.contextMode]: !webPortalMode,
                [styles.chatHistoryOpen]: historyOpen,
            })}
        >
            <motion.div
                id="chat-history-pane"
                aria-expanded={historyOpen}
                aria-hidden={!historyOpen}
                animate={historyOpen ? 'opened' : 'closed'}
                className={styles.history}
                {...motionProps}
            >
                {chatHistoryEnabled && <ChatHistory />}
            </motion.div>
            <Messages activeConversationId={activeConversationId} />
        </div>
    );
};
